import React, { FC, useState, useRef, useEffect, useCallback } from 'react';
import { Spinner } from '@chakra-ui/react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IProgrammingExercise } from '../../../models/programming';
import styles from './WorkoutBuilderModal.module.scss';

interface ExerciseSearchInputProps {
  value: string;
  onSelect: (exercise: IProgrammingExercise) => void;
  onChange: (query: string) => void;
}

const ExerciseSearchInput: FC<ExerciseSearchInputProps> = ({
  value,
  onSelect,
  onChange,
}) => {
  const { getProgrammingExercises } = useEndpoints();
  const [results, setResults] = useState<IProgrammingExercise[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = useCallback(
    (query: string) => {
      onChange(query);
      if (!query.trim()) {
        setResults([]);
        setOpen(false);
        return;
      }
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        try {
          setLoading(true);
          const { data } = await getProgrammingExercises({
            search: query,
            lang: 'it',
            limit: 10,
          });
          setResults(data.docs);
          setOpen(true);
        } catch {
          // silently ignore
        } finally {
          setLoading(false);
        }
      }, 300);
    },
    [getProgrammingExercises, onChange],
  );

  const getExName = (ex: IProgrammingExercise) =>
    ex.name.it || ex.name.en || '';

  return (
    <div className={styles.exerciseSearchWrapper} ref={wrapperRef}>
      <input
        type="text"
        className={styles.exerciseSearchInput}
        placeholder="Cerca esercizio..."
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => {
          if (results.length > 0) setOpen(true);
        }}
      />
      {loading && (
        <div className={styles.exerciseSearchSpinner}>
          <Spinner size="xs" />
        </div>
      )}
      {open && results.length > 0 && (
        <ul className={styles.exerciseDropdown}>
          {results.map((ex) => (
            <li
              key={ex._id}
              className={styles.exerciseDropdownItem}
              onMouseDown={() => {
                onSelect(ex);
                setOpen(false);
                setResults([]);
              }}
            >
              {getExName(ex)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ExerciseSearchInput;
